import { formatCell } from '../utils/numbers';
import { trackMixpanelEvent } from '../utils/general';

const FILTERS_TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS.FILTERS';

export const Dimensions = {
    APP: 'app',
    SOURCE: 'source',
    PLATFORM: 'platform',
    OS: 'os',
    CAMPAIGN: 'unified_campaign_name',
    COUNTRY: 'country_field',
};

export const DimensionConfig = {
    [Dimensions.APP]: { label: 'STATIC.APP', apiName: 'app' },
    [Dimensions.SOURCE]: { label: `${FILTERS_TRANSLATION_PREFIX}.SOURCE`, apiName: 'source' },
    [Dimensions.PLATFORM]: { label: 'STATIC.PLATFORM', apiName: 'platform' },
    [Dimensions.OS]: { label: 'STATIC.OS', apiName: 'os' },
    [Dimensions.CAMPAIGN]: { label: 'STATIC.CAMPAIGN', apiName: 'unified_campaign_name' },
    [Dimensions.COUNTRY]: { label: 'STATIC.COUNTRY', apiName: 'country_field' },
};

export const DIMENSIONS_OPTIONS = Object.entries(DimensionConfig).map(([value, { label }]) => ({
    value,
    label,
}));

const FILTERS_DIMENSIONS = [Dimensions.APP, Dimensions.SOURCE, Dimensions.COUNTRY];

export const FILTERS_OPTIONS = FILTERS_DIMENSIONS.map(key => ({
    value: key,
    label: DimensionConfig[key].label,
    apiName: DimensionConfig[key].apiName,
}));

export const MetricType = {
    COLOR: 'COLOR',
    SIZE: 'SIZE',
};

export const METRIC_API_TYPE = {
    COHORT: 'cohort',
    METRIC: 'metric',
};

export const MetricsConfig = {
    revenue__0d: {
        label: 'STATIC.REVENUE_0D',
        types: [MetricType.COLOR],
        apiName: 'revenue',
        apiType: METRIC_API_TYPE.COHORT,
        period: '0d',
    },
    revenue__1d: {
        label: 'STATIC.REVENUE_1D',
        types: [MetricType.COLOR],
        apiName: 'revenue',
        apiType: METRIC_API_TYPE.COHORT,
        period: '1d',
    },
    revenue__7d: {
        label: 'STATIC.REVENUE_7D',
        types: [MetricType.COLOR],
        apiName: 'revenue',
        apiType: METRIC_API_TYPE.COHORT,
        period: '7d',
    },
    revenue__14d: {
        label: 'STATIC.REVENUE_14D',
        types: [MetricType.COLOR],
        apiName: 'revenue',
        apiType: METRIC_API_TYPE.COHORT,
        period: '14d',
    },
    roi__0d: {
        label: 'STATIC.ROI_0D',
        types: [MetricType.COLOR],
        apiName: 'roi',
        apiType: METRIC_API_TYPE.COHORT,
        period: '0d',
    },
    roi__1d: {
        label: 'STATIC.ROI_1D',
        types: [MetricType.COLOR],
        apiName: 'roi',
        apiType: METRIC_API_TYPE.COHORT,
        period: '1d',
    },
    roi__7d: {
        label: 'STATIC.ROI_7D',
        types: [MetricType.COLOR],
        apiName: 'roi',
        apiType: METRIC_API_TYPE.COHORT,
        period: '7d',
    },
    roi__14d: {
        label: 'STATIC.ROI_14D',
        types: [MetricType.COLOR],
        apiName: 'roi',
        apiType: METRIC_API_TYPE.COHORT,
        period: '14d',
    },
    ad_monetization_roi__0d: {
        label: 'STATIC.AD_ROI_0D',
        types: [MetricType.COLOR],
        apiName: 'ad_monetization_roi',
        apiType: METRIC_API_TYPE.COHORT,
        period: '0d',
    },
    ad_monetization_roi__1d: {
        label: 'STATIC.AD_ROI_1D',
        types: [MetricType.COLOR],
        apiName: 'ad_monetization_roi',
        apiType: METRIC_API_TYPE.COHORT,
        period: '1d',
    },
    ad_monetization_roi__7d: {
        label: 'STATIC.AD_ROI_7D',
        types: [MetricType.COLOR],
        apiName: 'ad_monetization_roi',
        apiType: METRIC_API_TYPE.COHORT,
        period: '7d',
    },
    ad_monetization_roi__14d: {
        label: 'STATIC.AD_ROI_14D',
        types: [MetricType.COLOR],
        apiName: 'ad_monetization_roi',
        apiType: METRIC_API_TYPE.COHORT,
        period: '14d',
    },
    ocvr: { label: 'STATIC.OCVR', types: [MetricType.COLOR], apiName: 'ocvr', apiType: METRIC_API_TYPE.METRIC },
    ecpi: { label: 'STATIC.ECPI', types: [MetricType.COLOR], apiName: 'ecpi', apiType: METRIC_API_TYPE.METRIC },
    custom_installs: {
        label: 'STATIC.INSTALLS',
        types: [MetricType.COLOR, MetricType.SIZE],
        apiName: 'custom_installs',
        apiType: METRIC_API_TYPE.METRIC,
    },
    adn_cost: {
        label: 'STATIC.COST',
        types: [MetricType.COLOR, MetricType.SIZE],
        apiName: 'adn_cost',
        apiType: METRIC_API_TYPE.METRIC,
    },
    custom_impressions: {
        label: 'STATIC.IMPRESSIONS',
        types: [MetricType.SIZE],
        apiName: 'custom_impressions',
        apiType: METRIC_API_TYPE.METRIC,
    },
    custom_clicks: {
        label: 'STATIC.CLICKS',
        types: [MetricType.SIZE],
        apiName: 'custom_clicks',
        apiType: METRIC_API_TYPE.METRIC,
    },
};

export const ANOMALY_ANALYSIS_EVENT_PREFIX = 'Anomaly Analysis';

// Bookmark type discriminator for saved anomaly reports (see topbar/bookmarkUtils.js).
export const ANOMALY_ANALYSIS_REPORT_TYPE = 'anomalyAnalysis';

export const ANOMALY_EVENTS = {
    RUN_QUERY: 'Run Query',
    RUN_FAILED: 'Run Failed',
    SEGMENT_SELECTED: 'Segment Selected',
    CHART_ZOOM: 'Chart Zoom',
    RCA_RUN: 'RCA Run',
    RCA_CLOSE: 'RCA Close',
    TOP_MOVERS_CLOSE: 'Top Movers Close',
    TOP_MOVERS_OPEN: 'Top Movers Open',
    HISTORY_SESSION_OPEN: 'History Session Open',
    SAVE_REPORT: 'Save Report',
};

export const trackAnomalyEvent = (eventName, params) =>
    trackMixpanelEvent(ANOMALY_ANALYSIS_EVENT_PREFIX, eventName, params);

export const RCA_SOURCES = {
    CHART_SEGMENT: 'chart_segment',
    TOP_MOVERS: 'top_movers',
};

export const SidebarTabs = {
    SETUP: 'SETUP',
    HISTORY: 'HISTORY',
};

export const CHART_TYPE = 'sunburst';

export const COMPARE_PERIODS = {
    CURRENT: 'period1',
    PREVIOUS: 'period2',
};

export const DIMENSION_SEPARATOR = ':';
export const UNKNOWN_DIMENSION = 'Unknown';
export const NA_VALUE = 'N/A';
export const FULL_CHANGE_PCT = 100;
export const TIME_BREAKDOWN_ALL = 'all';
export const MAX_CACHE_ENTRIES = 20;

export const getAvailableOptions = (options, selectedValues, currentValue) => {
    const selectedSet = new Set(selectedValues.filter(v => v !== currentValue));
    return options.filter(option => !selectedSet.has(option.value));
};

export const ChartColorConfig = {
    NO_DATA: '#D1D5DB',
    GOOD: '#10b981',
    BAD: '#ef4444',
    NEUTRAL: '#FFFFFF',
};

export const formatChangeValue = (val, displayFormat) => {
    if (val == null) return formatCell(val, displayFormat);
    const sign = val > 0 ? '+' : val < 0 ? '-' : '';
    return sign + formatCell(Math.abs(val), displayFormat);
};

export const formatChangePct = pct => {
    if (!Number.isFinite(pct)) return 'N/A';
    const sign = pct > 0 ? '+' : pct < 0 ? '-' : '';
    return `${sign}${Math.abs(pct).toFixed(1)}%`;
};

const RCA_MESSAGE_TEMPLATE = [
    'Investigating a change in **{metric}** ({change})',
    'for {filters}',
    'from {start_date} to {end_date}',
    'compared to {previous_start} to {previous_end}',
];

export const buildRcaUserMessage = payload => {
    const values = {
        metric: payload.metric?.display_name || payload.metric?.api_name || 'metric',
        change: formatChangePct(payload.pct_change),
        filters: payload.filters.map(f => `${f.dimension} = ${f.values.join(', ')}`).join(', '),
        start_date: payload.start_date,
        end_date: payload.end_date,
        previous_start: payload.previous_period_start,
        previous_end: payload.previous_period_end,
    };
    return RCA_MESSAGE_TEMPLATE.join(' ').replace(/\{(\w+)}/g, (_, key) => values[key] ?? '');
};
